import Link from '@tiptap/extension-link'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import i18n from '@/util/config/i18n'
import { modifierKeyLabel } from '@/util/modifier-key'
import { openInNewTabWithFeedback } from '@/util/open-in-new-tab'
import { normalizeLinkHref } from '@/util/link-href'

/**
 * Tells the user that Ctrl/Cmd+click opens the link under the pointer, as a native tooltip on the rendered anchor. Shared
 * by the live editor and by static content, which has no editor yet. The hint is never part of the saved document.
 * A bookmark card is followed by a plain click, so it gets none.
 */
export function applyLinkOpenHint(target: EventTarget | null, root: Element): void {
  const anchor = (target as Element | null)?.closest?.('a')
  if (!anchor || !root.contains(anchor) || anchor.hasAttribute('title') || anchor.closest('.notch-bookmark__link'))
    return
  anchor.setAttribute('title', i18n.t('textEditor.linkOpenHint', { key: modifierKeyLabel() }) as string)
}

/**
 * Ctrl+click (Cmd+click on macOS) opens a link in a new tab while editing. A plain click only places the caret, so the
 * link can be edited from the bubble menu. A read-only editor is left to the browser, which follows links natively.
 */
function modClickOpenLink(): Plugin {
  return new Plugin({
    key: new PluginKey('modClickOpenLink'),
    props: {
      handleClick: (view, _pos, event) => {
        if (event.button !== 0 || !(event.ctrlKey || event.metaKey) || !view.editable) return false
        const anchor = (event.target as Element | null)?.closest?.('a')
        const href = anchor && view.dom.contains(anchor) ? anchor.getAttribute('href') : null
        if (!href) return false
        openInNewTabWithFeedback(normalizeLinkHref(href))
        return true
      },
      handleDOMEvents: {
        mouseover: (view, event) => {
          if (view.editable) applyLinkOpenHint(event.target, view.dom)
          return false
        }
      }
    }
  })
}

/** TipTap's default (openOnClick) opens the link on every click, even while editing, so a link could not be edited */
export const NotchLink = Link.extend({
  addProseMirrorPlugins() {
    return [...(this.parent?.() || []), modClickOpenLink()]
  }
}).configure({ openOnClick: false })
